import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { HelpPanel } from '../components/ui/help-panel';
import { contactsHelp } from '../lib/helpContent';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import BrandEmptyState from '../components/landing/BrandEmptyState';
import { 
  Users,
  Building2,
  Plus,
  Search,
  Edit,
  Phone,
  Mail,
  MoreHorizontal,
  Archive,
  RotateCcw,
  Trash2,
  MapPin,
  Loader2,
  MessageSquare,
  Send,
  Clock,
  User as UserIcon,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { apiFetch } from '../fetchInterceptor';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import ContactArchiveDeleteDialog from '../components/ContactArchiveDeleteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const formatAddress = (contact) => {
  const parts = [
    contact.street_address,
    contact.city,
    contact.state && contact.zip_code ? `${contact.state} ${contact.zip_code}` : contact.state || contact.zip_code,
    contact.country !== 'US' ? contact.country : null
  ].filter(Boolean);
  return parts.join(', ');
};

// Notes Timeline Component
const NotesTimeline = ({ contactId, contactName }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest');
  
  const fetchNotes = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/contacts/${contactId}/notes?sort=${sortOrder}`
      );
      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  }, [contactId, sortOrder]);
  
  useEffect(() => {
    if (contactId) {
      fetchNotes();
    }
  }, [fetchNotes, contactId]);
  
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/contacts/${contactId}/notes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: newNote })
        }
      );
      
      if (response.ok) {
        setNewNote('');
        fetchNotes();
        toast.success('Note added');
      } else {
        toast.error('Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Notes
        </h3>
        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Add Note Input */}
      <form onSubmit={handleAddNote} className="flex gap-2">
        <Input
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note..."
          className="flex-1"
          data-testid="new-note-input"
        />
        <Button type="submit" disabled={submitting || !newNote.trim()} data-testid="add-note-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
      
      {/* Notes List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>No notes yet</p>
          <p className="text-sm">Add your first note above</p>
        </div>
      ) : (
        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-3">
            {notes.map((note, idx) => (
              <div 
                key={note.note_id} 
                className="relative pl-6 pb-4 border-l-2 border-muted last:border-l-0"
                data-testid={`note-${note.note_id}`}
              >
                {/* Timeline dot */}
                <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-primary" />
                
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <UserIcon className="w-3 h-3" />
                      {note.created_by_name || 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(note.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

// Contact Detail uses new overlay component (imported at top)

// Main Contacts Page
export default function Contacts() {
  const { currentWorkspace, userRole } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const [activeTab, setActiveTab] = useState(typeParam === 'vendor' ? 'vendors' : 'customers');

  // Keep tab in sync with URL query param so sidebar links work correctly
  useEffect(() => {
    if (!typeParam) {
      // Default to customer view on bare /contacts
      setSearchParams({ type: 'customer' }, { replace: true });
      return;
    }
    const desired = typeParam === 'vendor' ? 'vendors' : 'customers';
    if (desired !== activeTab) setActiveTab(desired);
  }, [typeParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (val) => {
    setActiveTab(val);
    setSearchParams({ type: val === 'vendors' ? 'vendor' : 'customer' }, { replace: true });
  };

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'archived' | 'all'

  const isAdmin = ['owner', 'admin'].includes((userRole || '').toLowerCase());

  // Archive / restore / delete dialog state
  const [dialogState, setDialogState] = useState({ open: false, mode: null, contact: null });
  const openDialog = (mode, contact) => setDialogState({ open: true, mode, contact });
  const closeDialog = () => setDialogState((s) => ({ ...s, open: false }));

  const fetchContacts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      const type = activeTab === 'customers' ? 'customer' : 'vendor';
      const url = `${API_URL}/api/contacts?workspace_id=${currentWorkspace.workspace_id}&contact_type=${type}&status=${statusFilter}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, activeTab, statusFilter]);
  
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);
  
  // Client-side filtering and sorting
  const filteredAndSortedContacts = React.useMemo(() => {
    let filtered = [...contacts];
    
    // Filter by search term (client-side additional filtering)
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(contact =>
        contact.name?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.phone?.toLowerCase().includes(searchLower) ||
        contact.city?.toLowerCase().includes(searchLower) ||
        contact.state?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle null/undefined
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // Numeric comparison for balance and payment_terms
        if (sortConfig.key === 'balance' || sortConfig.key === 'payment_terms') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // String comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [contacts, search, sortConfig]);
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };
  
  const handleEdit = (contact) => {
    navigate(`/contacts/${contact.contact_id}/edit`);
  };
  
  const handleNew = () => {
    const type = activeTab === 'customers' ? 'customer' : 'vendor';
    navigate(`/contacts/new?type=${type}`);
  };
  
  const handleViewDetail = (contact) => {
    navigate(`/contacts/${contact.contact_id}`);
  };
  
  const renderContactsTable = (data) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead 
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => handleSort('name')}
            data-testid="sort-name"
          >
            <div className="flex items-center">
              Name
              {getSortIcon('name')}
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => handleSort('email')}
            data-testid="sort-email"
          >
            <div className="flex items-center">
              Contact
              {getSortIcon('email')}
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => handleSort('city')}
            data-testid="sort-location"
          >
            <div className="flex items-center">
              Location
              {getSortIcon('city')}
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => handleSort('payment_terms')}
            data-testid="sort-terms"
          >
            <div className="flex items-center">
              Terms
              {getSortIcon('payment_terms')}
            </div>
          </TableHead>
          <TableHead 
            className="text-right cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => handleSort('balance')}
            data-testid="sort-balance"
          >
            <div className="flex items-center justify-end">
              Balance
              {getSortIcon('balance')}
            </div>
          </TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map(contact => (
          <TableRow 
            key={contact.contact_id} 
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => handleViewDetail(contact)}
            data-testid={`contact-${contact.contact_id}`}
          >
            <TableCell>
              <div className="flex items-center gap-1.5">
                {contact.entity_type === 'organization' 
                  ? <Building2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  : <UserIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                }
                <p className="font-medium">{contact.name}</p>
              </div>
            </TableCell>
            <TableCell>
              <div className="space-y-1">
                {contact.email && (
                  <div className="flex items-center gap-1 text-sm">
                    <Mail className="w-3 h-3" /> {contact.email}
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-1 text-sm">
                    <Phone className="w-3 h-3" /> {contact.phone}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              {contact.city && (
                <span className="text-sm">{contact.city}{contact.state ? `, ${contact.state}` : ''}</span>
              )}
            </TableCell>
            <TableCell>Net {contact.payment_terms}</TableCell>
            <TableCell className="text-right font-mono">
              <span className={cn(
                contact.balance > 0 && contact.contact_type === 'customer' && 'text-amber-600',
                contact.balance > 0 && contact.contact_type === 'vendor' && 'text-rose-600'
              )}>
                {formatCurrency(contact.balance)}
              </span>
            </TableCell>
            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(contact)} data-testid={`edit-${contact.contact_id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`actions-${contact.contact_id}`}>
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {contact.status === 'archived' ? (
                      <DropdownMenuItem onClick={() => openDialog('restore', contact)} data-testid={`restore-${contact.contact_id}`}>
                        <RotateCcw className="w-4 h-4 mr-2 text-emerald-500" />
                        Restore
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => openDialog('archive', contact)} data-testid={`archive-${contact.contact_id}`}>
                        <Archive className="w-4 h-4 mr-2 text-amber-500" />
                        Archive
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => openDialog('delete', contact)}
                          className="text-rose-600 focus:text-rose-700 focus:bg-rose-50 dark:focus:bg-rose-950/30"
                          data-testid={`delete-${contact.contact_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete permanently
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button size="sm" variant="ghost" onClick={() => handleViewDetail(contact)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  
  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Customers & Vendors</h1>
          <p className="text-muted-foreground">Manage your customers and vendors</p>
        </div>
        <HelpPanel {...contactsHelp} />
      </div>
      
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="customers" className="flex items-center gap-2" data-testid="customers-tab">
              <Users className="w-4 h-4" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="vendors" className="flex items-center gap-2" data-testid="vendors-tab">
              <Building2 className="w-4 h-4" />
              Vendors
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active" data-testid="filter-active">Active</SelectItem>
                <SelectItem value="archived" data-testid="filter-archived">Archived</SelectItem>
                <SelectItem value="all" data-testid="filter-all">All</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, phone, city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-[280px]"
                data-testid="search-contacts"
              />
            </div>
            <Button onClick={handleNew} data-testid="new-contact-btn">
              <Plus className="w-4 h-4 mr-2" />
              New {activeTab === 'customers' ? 'Customer' : 'Vendor'}
            </Button>
          </div>
        </div>
        
        <TabsContent value="customers" className="mt-6">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : filteredAndSortedContacts.length === 0 ? (
                search ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No customers match your search</p>
                    <p className="text-sm text-muted-foreground mt-1">Try a different search term</p>
                  </div>
                ) : (
                  <BrandEmptyState
                    overline="Customers · Get Started"
                    title="Add your first customer."
                    accent="customer."
                    description="Track receivables, send invoices, and watch payment timing in one place. Connect a bank to auto-match incoming payments to customers."
                    icon={Users}
                    tone="blue"
                    primaryCta={{ label: 'Add Customer', onClick: handleNew, testId: 'empty-add-customer' }}
                    testId="customers-empty-state"
                    className="border-0 rounded-none"
                  />
                )
              ) : (
                renderContactsTable(filteredAndSortedContacts)
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="vendors" className="mt-6">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : filteredAndSortedContacts.length === 0 ? (
                search ? (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No vendors match your search</p>
                    <p className="text-sm text-muted-foreground mt-1">Try a different search term</p>
                  </div>
                ) : (
                  <BrandEmptyState
                    overline="Vendors · Get Started"
                    title="Add your first vendor."
                    accent="vendor."
                    description="Track payables, automate bill entry from scanned PDFs, and keep 1099-eligible spend pre-tagged for year-end. Auto-match outgoing payments."
                    icon={Building2}
                    tone="amber"
                    primaryCta={{ label: 'Add Vendor', onClick: handleNew, testId: 'empty-add-vendor' }}
                    testId="vendors-empty-state"
                    className="border-0 rounded-none"
                  />
                )
              ) : (
                renderContactsTable(filteredAndSortedContacts)
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ContactArchiveDeleteDialog
        open={dialogState.open}
        onOpenChange={(v) => setDialogState((s) => ({ ...s, open: v }))}
        contact={dialogState.contact}
        mode={dialogState.mode}
        viewerIsAdmin={isAdmin}
        onSuccess={fetchContacts}
      />
    </div>
  );
}

